import { DateTime } from 'luxon';

import { AlertType } from './domain/models/alert-type';

export interface AlertColorOption
{
    /** Token CSS (custom property) de la variante claire utilisee en fond de bandeau. */
    token: string;
    /** Libelle affiche dans l'administration des alertes (module actuellement redige en francais). */
    label: string;
}

export interface AlertColorSource
{
    alertType?: AlertType | null;
    backgroundColor?: string | null;
}

/** Champs d'une alerte dont le tri d'affichage a besoin. */
export interface AlertRankSource
{
    alertType?: AlertType | null;
    since?: DateTime | null;
}

export class NotfAlertConstants
{
    /**
     * Couleur du bandeau avant l'apparition des types d'alerte.
     * Elle reste appliquee aux alertes demunies de type et de couleur personnalisee.
     */
    public static readonly legacyColorToken: string = '--color-primary-normal';

    /**
     * Couleurs proposees a l'administration : uniquement des variantes claires des peaux light et prune,
     * dont le contraste avec le texte sombre du bandeau est AAA. La liste doit rester alignee sur
     * AlertBackgroundColor cote backend.
     */
    public static readonly colorOptions: AlertColorOption[] = [
        {
            token: '--color-msg-danger-light',
            label: 'Rouge'
        },
        {
            token: '--color-msg-orange-light',
            label: 'Orange'
        },
        {
            token: '--color-msg-warning2-light',
            label: 'Ambre'
        },
        {
            token: '--color-msg-yellow-light',
            label: 'Jaune'
        },
        {
            token: '--color-msg-success-light',
            label: 'Vert'
        },
        {
            token: '--color-msg-blue-light',
            label: 'Bleu'
        },
        {
            token: '--color-msg-info-light',
            label: 'Violet'
        },
        {
            token: '--color-msg-grey-light',
            label: 'Gris'
        }
    ];

    /**
     * Couleur appliquee au bandeau quand aucune couleur personnalisee n'est enregistree : l'information est
     * jaune, afin de rester lisible sans alarmer (le rouge et l'orange sont reserves aux alertes critiques
     * et de maintenance). Tous ces tokens sont proposes a l'administration, ou l'administrateur peut en
     * choisir un autre.
     */
    public static readonly defaultColorTokenByType: { [type: string]: string } = {
        [AlertType.Critical]: '--color-msg-danger-light',
        [AlertType.Maintenance]: '--color-msg-orange-light',
        [AlertType.Information]: '--color-msg-yellow-light'
    };

    private static readonly labelByType: { [type: string]: string } = {
        [AlertType.Critical]: 'Critique',
        [AlertType.Maintenance]: 'Maintenance',
        [AlertType.Information]: 'Information'
    };

    private static readonly iconByType: { [type: string]: string } = {
        [AlertType.Critical]: 'fas fa-exclamation-triangle',
        [AlertType.Maintenance]: 'fas fa-wrench',
        [AlertType.Information]: 'fal fa-info-circle'
    };

    /**
     * Reference CSS a appliquer pour peindre une couleur de la liste blanche : le rendu suit ainsi la
     * peau active, sans jamais dupliquer un code hexadecimal.
     */
    public static tokenCss (token: string): string
    {
        return `var(${token})`;
    }

    public static isAllowedColor (token: string): boolean
    {
        if (!token)
        {
            return false;
        }

        return NotfAlertConstants.colorOptions.some((option: AlertColorOption): boolean => option.token === token);
    }

    public static defaultColorFor (type: AlertType): string
    {
        return NotfAlertConstants.defaultColorTokenByType[type] ?? null;
    }

    /**
     * Cascade de couleur du bandeau : couleur personnalisee, sinon couleur par defaut du type,
     * sinon rendu historique.
     */
    public static colorFor (alert: AlertColorSource): string
    {
        if (NotfAlertConstants.isAllowedColor(alert?.backgroundColor))
        {
            return alert.backgroundColor;
        }

        return NotfAlertConstants.defaultColorFor(alert?.alertType) ?? NotfAlertConstants.legacyColorToken;
    }

    public static colorCss (alert: AlertColorSource): string
    {
        return NotfAlertConstants.tokenCss(NotfAlertConstants.colorFor(alert));
    }

    /**
     * Rang de priorite d'affichage : 0 = alerte a afficher en premier. Une alerte pourvue d'une nature prime
     * toujours sur une alerte heritee, qui n'en a pas. Doit rester aligne sur SeverityRank cote backend.
     */
    public static severity (type: AlertType): number
    {
        switch (type)
        {
            case AlertType.Critical:
                return 0;
            case AlertType.Maintenance:
                return 1;
            case AlertType.Information:
                return 2;
            default:
                return 3;
        }
    }

    /**
     * Ordre d'affichage des alertes a afficher : nature la plus critique d'abord, puis date de debut la
     * plus ancienne. Miroir du tri de GetActiveAlerts cote backend, a appliquer ici aussi parce que la
     * meme collection est remplacee par la liste d'administration, qui est triee par date.
     */
    public static compareBySeverity (alerta: AlertRankSource, alertb: AlertRankSource): number
    {
        const ranking: number = NotfAlertConstants.severity(alerta?.alertType) - NotfAlertConstants.severity(alertb?.alertType);

        if (ranking !== 0 || !alerta?.since || !alertb?.since)
        {
            return ranking;
        }

        return alerta.since < alertb.since ? -1 : (alerta.since > alertb.since ? 1 : 0);
    }

    public static label (type: AlertType): string
    {
        return NotfAlertConstants.labelByType[type] ?? 'Historique';
    }

    public static icon (type: AlertType): string
    {
        return NotfAlertConstants.iconByType[type] ?? 'fal fa-info-circle';
    }
}


